import { appApiHandler } from "../api/appApiHandler";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Resolves the number of available colors for a product from many possible
 * API shapes. Works whether the list endpoint returns a `colors` array on
 * the product, a numeric count field, or only an image array where each
 * image has a `color` object.
 */
export const getColorCount = (item) => {
  if (!item) return 0;

  if (Array.isArray(item.colors)) return item.colors.length;
  if (Array.isArray(item.color)) return item.color.length;
  if (Array.isArray(item.available_colors)) return item.available_colors.length;

  if (typeof item.color_count === "number") return item.color_count;
  if (typeof item.colors_count === "number") return item.colors_count;
  if (typeof item.total_colors === "number") return item.total_colors;

  if (Array.isArray(item.image)) {
    const ids = new Set();
    item.image.forEach((img) => {
      const id =
        img?.color?._id ||
        img?.color?.id ||
        img?.color_id ||
        (typeof img?.color === "string" ? img.color : null);
      if (id) ids.add(id);
    });
    if (ids.size > 0) return ids.size;
  }

  return 0;
};

/**
 * Fetches product details in small batches so the home page does not open
 * 30–40 parallel GET_PRODUCT_DETAILS calls (which freezes the tab and slows the API).
 */
export const enrichProductsWithColors = async (products, options = {}) => {
  if (!Array.isArray(products) || products.length === 0) return products;

  const concurrency = Math.max(
    1,
    Math.min(5, Number(options.concurrency) || 3)
  );

  const interChunkDelayMs =
    options.interChunkDelayMs != null
      ? Math.max(0, Number(options.interChunkDelayMs))
      : 150;

  const enrichOne = async (product) => {
    if (!product?._id) return product;
    if (Array.isArray(product.colors) && product.colors.length > 0) {
      return product;
    }
    try {
      const response = await appApiHandler.getProductDetails({
        product_id: product._id,
      });
      if (response?.status === 200 && response?.data) {
        const detail = response.data;
        return {
          ...product,
          colors: Array.isArray(detail.colors) ? detail.colors : [],
          mrp: product.mrp ?? detail.mrp,
          original_price: product.original_price ?? detail.original_price,
          stock: product.stock ?? detail.stock,
          in_stock: product.in_stock ?? detail.in_stock,
        };
      }
    } catch (err) {
      // Silent fallback – list product still renders, just without colors.
    }
    return product;
  };

  const out = [];
  for (let i = 0; i < products.length; i += concurrency) {
    const chunk = products.slice(i, i + concurrency);
    const part = await Promise.all(chunk.map((p) => enrichOne(p)));
    out.push(...part);
    if (i + concurrency < products.length && interChunkDelayMs > 0) {
      await sleep(interChunkDelayMs);
    }
  }

  return out;
};
